import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiMenu, FiMoon, FiSun, FiX } from "react-icons/fi";
import "./Navbar.css";

const mobileMenuVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.18,
      ease: "easeIn",
    },
  },
};

const mobilePanelVariants = {
  hidden: {
    opacity: 0,
    y: 28,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.28,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    y: 18,
    scale: 0.98,
    transition: {
      duration: 0.18,
      ease: "easeIn",
    },
  },
};

type MenuItem = {
  text: string;
  link: string;
};

type NavbarProps = {
  activeSection: string;
  brandName: string;
  isDarkMode: boolean;
  menuItems: MenuItem[];
  onNavigate: (id: string) => void;
  onToggleTheme: () => void;
};

const Navbar: React.FC<NavbarProps> = ({
  activeSection,
  brandName,
  isDarkMode,
  menuItems,
  onNavigate,
  onToggleTheme,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      return undefined;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  const closeMenu = () => {
    setIsOpen(false);
  };

  const toggleMenu = () => setIsOpen(!isOpen);
  const handleNavigate = (id: string) => {
    onNavigate(id);
    closeMenu();
  };

  return (
    <>
      <motion.nav className="navbar">
        <div className="navbar-shell">
          <button
            className="navbar-brand"
            onClick={() => onNavigate(menuItems[0]?.link ?? "hero")}
            type="button"
          >
            {brandName}
          </button>

          <ul className="navbar-desktop-nav">
            {menuItems.map((item) => (
              <li key={item.link}>
                <button
                  onClick={() => onNavigate(item.link)}
                  type="button"
                  className={activeSection === item.link ? "navbar-link navbar-link-active" : "navbar-link"}
                >
                  {item.text}
                </button>
              </li>
            ))}
            <li>
              <button
                onClick={onToggleTheme}
                type="button"
                aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                className="navbar-theme-toggle"
              >
                {isDarkMode ? <FiSun size={16} /> : <FiMoon size={16} />}
              </button>
            </li>
          </ul>

          <button
            aria-controls="mobile-navigation"
            aria-expanded={isOpen}
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            className="navbar-mobile-toggle"
            onClick={toggleMenu}
            type="button"
          >
            {isOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            className="navbar-mobile-menu"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={mobileMenuVariants}
            onClick={closeMenu}
          >
            <motion.div
              aria-label="Mobile navigation"
              aria-modal="true"
              className="navbar-mobile-panel"
              id="mobile-navigation"
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              variants={mobilePanelVariants}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="navbar-mobile-header">
                <span className="navbar-mobile-eyebrow">Navigation</span>
                <button
                  aria-label="Close navigation menu"
                  className="navbar-mobile-close"
                  onClick={closeMenu}
                  type="button"
                >
                  <FiX />
                </button>
              </div>

              <div className="navbar-mobile-body">
                <button
                  className="navbar-mobile-brand"
                  onClick={() => handleNavigate(menuItems[0]?.link ?? "hero")}
                  type="button"
                >
                  {brandName}
                </button>

                <ul className="navbar-mobile-nav">
                  {menuItems.map((item) => (
                    <li key={item.link} className="navbar-mobile-nav-item">
                      <button
                        onClick={() => handleNavigate(item.link)}
                        type="button"
                        className={
                          activeSection === item.link
                            ? "navbar-mobile-link navbar-mobile-link-active"
                            : "navbar-mobile-link"
                        }
                      >
                        {item.text}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="navbar-mobile-footer">
                <div className="navbar-mobile-theme-row">
                  <div className="navbar-mobile-theme-copy">
                    <span className="navbar-mobile-theme-label">Theme</span>
                    <span className="navbar-mobile-theme-text">
                      {isDarkMode ? "Switch to light" : "Switch to dark"}
                    </span>
                  </div>
                  <button
                    onClick={onToggleTheme}
                    type="button"
                    aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                    className="navbar-theme-toggle navbar-theme-toggle-mobile"
                  >
                    {isDarkMode ? <FiSun size={16} /> : <FiMoon size={16} />}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
